/**
 * Gobodo Workflow Rule Service
 *
 * CRUD for WorkflowRule__c via Gobodo API. Maps app WorkflowRule type to/from
 * Gobodo record shape (JSON fields for criteria and actions).
 */

import type { WorkflowRule } from '../../types/workflowRules';
import {
  getGobodoRecords,
  getGobodoRecordById,
  createGobodoRecord,
  updateGobodoRecord,
  deleteGobodoRecord
} from './gobodoRecordService';
import { ensureObjectTypeExists } from '../../utils/gobodoObjectTypeVerifier';
import { getCustomSuffix } from '../../config/gobodoConfig';

const getObjectType = () => `WorkflowRule${getCustomSuffix()}`;

export async function ensureWorkflowRuleObjectExists(): Promise<void> {
  await ensureObjectTypeExists(getObjectType(), {
    label: 'Workflow Rule',
    pluralLabel: 'Workflow Rules',
    description: 'Stores workflow rule definitions (triggers, criteria, actions) for Process Automation.',
    icon: 'Zap',
    category: 'custom',
    isCustom: true,
    isActive: true
  });
}

const FIELD = {
  NAME: 'Name__c',
  DESCRIPTION: 'Description__c',
  OBJECT_API_NAME: 'ObjectApiName__c',
  IS_ACTIVE: 'IsActive__c',
  TRIGGER_TYPE: 'TriggerType__c',
  EVALUATION_CRITERIA_JSON: 'EvaluationCriteriaJson__c',
  ACTIONS_JSON: 'ActionsJson__c',
  CREATED_DATE: 'CreatedDate__c',
  LAST_MODIFIED: 'LastModified__c',
  CREATED_BY: 'CreatedBy__c',
  LAST_MODIFIED_BY: 'LastModifiedBy__c',
  EXECUTION_COUNT: 'ExecutionCount__c',
  LAST_EXECUTED: 'LastExecuted__c'
} as const;

function workflowRuleToGobodoRecord(rule: Partial<WorkflowRule>): Record<string, any> {
  const record: Record<string, any> = {
    [FIELD.NAME]: rule.name ?? '',
    [FIELD.DESCRIPTION]: rule.description ?? '',
    [FIELD.OBJECT_API_NAME]: rule.objectApiName ?? '',
    [FIELD.IS_ACTIVE]: rule.isActive ?? true,
    [FIELD.TRIGGER_TYPE]: rule.triggerType ?? 'onCreateAndUpdate',
    [FIELD.EVALUATION_CRITERIA_JSON]: rule.evaluationCriteria
      ? JSON.stringify(rule.evaluationCriteria)
      : JSON.stringify({ operator: 'AND', conditions: [] }),
    [FIELD.ACTIONS_JSON]: rule.actions ? JSON.stringify(rule.actions) : '[]',
    [FIELD.CREATED_BY]: rule.createdBy ?? '',
    [FIELD.LAST_MODIFIED_BY]: rule.lastModifiedBy ?? ''
  };
  if (rule.createdDate) record[FIELD.CREATED_DATE] = rule.createdDate;
  if (rule.lastModified) record[FIELD.LAST_MODIFIED] = rule.lastModified;
  if (rule.executionCount !== undefined) record[FIELD.EXECUTION_COUNT] = rule.executionCount;
  if (rule.lastExecuted) record[FIELD.LAST_EXECUTED] = rule.lastExecuted;
  return record;
}

function gobodoRecordToWorkflowRule(record: Record<string, any>): WorkflowRule {
  const id = record.id != null ? String(record.id) : '';
  let evaluationCriteria = { operator: 'AND' as const, conditions: [] };
  try {
    const raw = record[FIELD.EVALUATION_CRITERIA_JSON];
    if (raw && typeof raw === 'string') evaluationCriteria = JSON.parse(raw);
    else if (raw && typeof raw === 'object') evaluationCriteria = raw;
  } catch {
    // keep default
  }
  let actions: WorkflowRule['actions'] = [];
  try {
    const raw = record[FIELD.ACTIONS_JSON];
    if (raw && typeof raw === 'string') actions = JSON.parse(raw);
    else if (Array.isArray(raw)) actions = raw;
  } catch {
    // keep default
  }
  return {
    id,
    name: record[FIELD.NAME] ?? '',
    description: record[FIELD.DESCRIPTION] ?? undefined,
    objectApiName: record[FIELD.OBJECT_API_NAME] ?? '',
    isActive: Boolean(record[FIELD.IS_ACTIVE]),
    triggerType: (record[FIELD.TRIGGER_TYPE] as WorkflowRule['triggerType']) ?? 'onCreateAndUpdate',
    evaluationCriteria,
    actions,
    createdDate: record[FIELD.CREATED_DATE] ?? new Date().toISOString(),
    createdBy: record[FIELD.CREATED_BY] ?? '',
    lastModified: record[FIELD.LAST_MODIFIED] ?? new Date().toISOString(),
    lastModifiedBy: record[FIELD.LAST_MODIFIED_BY] ?? '',
    executionCount: record[FIELD.EXECUTION_COUNT] ?? 0,
    lastExecuted: record[FIELD.LAST_EXECUTED] ?? undefined
  };
}

export async function getWorkflowRules(): Promise<WorkflowRule[]> {
  await ensureWorkflowRuleObjectExists();
  const { records } = await getGobodoRecords(getObjectType());
  return records.map(r => gobodoRecordToWorkflowRule(r));
}

export async function getWorkflowRuleById(id: string): Promise<WorkflowRule | null> {
  const record = await getGobodoRecordById(id);
  if (!record) return null;
  return gobodoRecordToWorkflowRule(record);
}

export async function createWorkflowRule(
  rule: Omit<WorkflowRule, 'id' | 'createdDate' | 'lastModified'>
): Promise<WorkflowRule> {
  const now = new Date().toISOString();
  const payload = workflowRuleToGobodoRecord({
    ...rule,
    createdDate: now,
    lastModified: now
  });
  const created = await createGobodoRecord(getObjectType(), payload);
  return gobodoRecordToWorkflowRule(created);
}

export async function updateWorkflowRule(
  id: string,
  updates: Partial<WorkflowRule>
): Promise<WorkflowRule | null> {
  const existing = await getGobodoRecordById(id);
  if (!existing) return null;
  const payload = workflowRuleToGobodoRecord(updates);
  const updated = await updateGobodoRecord(id, payload);
  return gobodoRecordToWorkflowRule(updated);
}

export async function deleteWorkflowRule(id: string): Promise<boolean> {
  await deleteGobodoRecord(id);
  return true;
}
